import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import PostListRow from "@/components/PostListRow";

const MyWordsDialog = ({ open, onOpenChange, onPickPost }) => {
  const [words, setWords] = useState([]);
  const [loadingWords, setLoadingWords] = useState(false);
  const [active, setActive] = useState(null); // currently selected word
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  useEffect(() => {
    if (!open) {
      setActive(null);
      setPosts([]);
      return;
    }
    if (words.length) return;
    setLoadingWords(true);
    (async () => {
      try {
        const { data } = await api.get("/stats/word-cloud", { params: { limit: 80 } });
        setWords(data.words || []);
      } finally {
        setLoadingWords(false);
      }
    })();
  }, [open, words.length]);

  const pickWord = async (w) => {
    setActive(w);
    setPosts([]);
    setLoadingPosts(true);
    try {
      const { data } = await api.get("/posts-by-word", { params: { w, per_page: 200 } });
      setPosts(data.items || []);
    } finally {
      setLoadingPosts(false);
    }
  };

  const max = words.length ? Math.max(...words.map((w) => w.count)) : 1;
  const min = words.length ? Math.min(...words.map((w) => w.count)) : 1;
  const seeded = (i, salt = 0) => {
    const x = Math.sin((i + 1) * 9301 + salt) * 43758.5453;
    return x - Math.floor(x);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-[#1a1918]/85 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="my-words-dialog"
          className="fixed left-[50%] top-[50%] z-50 grid w-[95vw] max-w-3xl translate-x-[-50%] translate-y-[-50%] bg-[#f9f8f6] shadow-2xl border border-[#e5e1d8] max-h-[90vh] overflow-y-auto"
        >
          <DialogPrimitive.Close
            data-testid="my-words-close"
            className="absolute right-3 top-3 z-10 p-2 bg-[#f9f8f6]/80 hover:bg-[#1a1918] hover:text-[#f9f8f6] transition-colors"
            aria-label="chiudi"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          <div className="px-5 sm:px-10 py-8 sm:py-10">
            {!active ? (
              <>
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#c5a873] mb-2">le mie parole</p>
                <DialogTitle className="font-serif text-2xl sm:text-4xl tracking-tight leading-none font-light text-[#1a1918] mb-2">
                  Le parole che tornano
                </DialogTitle>
                <p className="font-serif italic text-[#4a4843] mb-8 leading-relaxed">
                  Tocca una parola per leggere tutti i post che la contengono.
                </p>
                {loadingWords ? (
                  <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
                ) : !words.length ? (
                  <p className="font-serif italic text-[#4a4843]">Ancora nessuna parola ricorrente.</p>
                ) : (
                  <div
                    data-testid="my-words-cloud"
                    className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-1 py-2"
                  >
                    {words.map((w, i) => {
                      const ratio = max === min ? 0.6 : (w.count - min) / (max - min);
                      const fontSize = 0.9 + ratio * 2.6;
                      const opacity = 0.45 + ratio * 0.55;
                      const rotate = (seeded(i) - 0.5) * 6;
                      const isAccent = seeded(i, 7) > 0.82;
                      const italic = seeded(i, 13) > 0.6;
                      return (
                        <button
                          key={w.word}
                          type="button"
                          data-testid={`my-word-${w.word}`}
                          onClick={() => pickWord(w.word)}
                          className={`font-serif leading-none cursor-pointer hover:text-[#c5a873] transition-colors px-1 ${italic ? "italic" : ""}`}
                          style={{
                            fontSize: `${fontSize}rem`,
                            opacity,
                            transform: `rotate(${rotate.toFixed(2)}deg)`,
                            color: isAccent ? "#c5a873" : "#1a1918",
                            fontWeight: ratio > 0.6 ? 500 : 400,
                          }}
                          title={`${w.word} — ${w.count}`}
                        >
                          {w.word}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="my-words-back"
                  onClick={() => { setActive(null); setPosts([]); }}
                  className="inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] hover:text-[#1a1918] mb-3"
                >
                  <ArrowLeft size={12} /> tutte le parole
                </button>
                <DialogTitle className="font-serif text-2xl sm:text-4xl tracking-tight leading-none font-light text-[#1a1918] mb-2">
                  <em className="font-light">«{active}»</em>
                </DialogTitle>
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#c5a873] mb-8">
                  {loadingPosts ? "…" : `${posts.length} ${posts.length === 1 ? "post" : "post"}`}
                </p>
                {loadingPosts ? (
                  <p className="font-sans text-sm text-[#4a4843]">caricamento…</p>
                ) : posts.length === 0 ? (
                  <p className="font-serif italic text-[#4a4843] py-10 text-center">
                    Nessun post contiene questa parola.
                  </p>
                ) : (
                  <ul
                    data-testid="my-words-results"
                    className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]"
                  >
                    {posts.map((p) => (
                      <li key={p.id} data-testid={`my-words-result-${p.id}`}>
                        <PostListRow
                          post={p}
                          onOpen={(id) => { onOpenChange(false); onPickPost(id); }}
                          variant="compact"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default MyWordsDialog;
